import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import Modal from 'react-bootstrap/Modal';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { MultiSelect } from 'react-multi-select-component';
import Loader from '../../../Components/Utility/Loader/Loader';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import { CareUserContext } from './provider/careUserContext';
import CustomDatePicker from '../../../Components/Utility/DatePicker/DatePicker';
import { customStyles, shortenandHoverString } from '../../../Components/helper';
import noImage from '../../../assets/images/no-image.png';
import UserInstructionImage from './UserInstructionImage';
import BomImageDropzone from '../../../Components/ImgDropzone/BomImageDropzone';
import { addUpdateUserInstructionDetailAction, getAllUserTabs } from './store/UserInstruction.action';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';

export default function TabDetail() {
  const childRef = useRef();
  const param = useParams();
  const dispatch = useDispatch();
  const [saveClicked, setSaveClicked] = useState(false);
  const [imageRowIndex, setImageRowIndex] = useState('');
  const { t } = useTranslation('common');
  const { state, handleRowSelection, handleSelectAll, rows, setState, handleDeleteMultiple } = useContext(CareUserContext);
  const setImage = (image) => {
    let imagePath = '';
    if (image?.includes('data:image/')) {
      imagePath = image;
    } else if (image) {
      imagePath = `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${image}`;
    } else {
      imagePath = noImage;
    }
    return imagePath;
  };
  const handleEditClick = (rowId) => {
    setState((prevState) => ({
      ...prevState,
      editingRowId: rowId,
      editMode: true,
    }));
  };
  const validateFields = (row) => {
    const mandatoryColumns = state?.columns?.filter((column) => {
      return column?.columnDetails?.columnType?.key !== 'Status' && column?.columnDetails?.isRequired;
    });
    let error = true;
    const requiredColumns = mandatoryColumns?.map((e) => e._id);
    const hasValue = requiredColumns?.every((element) => {
      return !!row?.find((e) => e?.columnId === element);
    });
    if (!hasValue) {
      error = false;
    } else {
      row?.forEach((element) => {
        if (requiredColumns?.includes(element?.columnId)) {
          if (element?.value) {
            const mandatoryColumnTypes = mandatoryColumns?.find((e) => e._id === element?.columnId)?.columnDetails;
            if (mandatoryColumnTypes?.columnType?.key === 'Textbox' && element?.value?.length > mandatoryColumnTypes?.characterLimit) {
              error = false;
            }
          } else {
            error = false;
          }
        }
      });
    }
    return error;
  };

  const handleChange = (value, rowIndex, columnId, fromDate) => {
    const copyTabData = cloneDeep(state?.tabData);
    const foundCell = copyTabData?.[rowIndex]?.find((cell) => cell?.columnId === columnId);
    if (!foundCell) {
      const sibling = copyTabData?.[rowIndex]?.[0];
      copyTabData?.[rowIndex].push({
        columnId,
        value,
        rowIndex: sibling?.rowIndex ?? rowIndex,
        rowId: sibling?.rowId,
        tabId: param?._id,
      });
    } else {
      copyTabData?.[rowIndex]?.forEach((element) => {
        if (element?.columnId === columnId) {
          element.value = value;
          if (fromDate) {
            element.isDate = true;
          }
        }
      });
    }

    setState((prevState) => ({
      ...prevState,
      tabData: copyTabData,
    }));
  };

  const handleSaveClick = async (row) => {
    setSaveClicked(true);
    if (validateFields(row)) {
      const params = {
        id: param?._id,
        rowDetails: row,
      };
      const res = await dispatch(addUpdateUserInstructionDetailAction(params, t));
      if (res?.code === 200) {
        await dispatch(getAllUserTabs(param?._id));
        setState((prevState) => ({
          ...prevState,
          editingRowId: '',
          addNewRow: false,
          editMode: false,
        }));
        setSaveClicked(false);
      }
    } else {
      toasterMsg('Please fill all mandatory fields', 'danger');
    }
  };

  const handleCancelClick = async (row, rowIndex) => {
    setSaveClicked(false);
    if (!row[0]?.rowId) {
      const copysTabData = cloneDeep(state?.tabData);
      const filterData = copysTabData?.filter((item, index) => index !== rowIndex);
      setState((prevState) => ({
        ...prevState,
        tabData: filterData,
        editingRowId: '',
        addNewRow: false,
      }));
    } else {
      await dispatch(getAllUserTabs(param?._id));
      setState((prevState) => ({
        ...prevState,
        editingRowId: '',
        addNewRow: false,
      }));
    }
  };

  const handleImageUpload = (rowIndex, columnId, value, dataRowIndex) => {
    setState((prevState) => ({
      ...prevState,
      showImageUpload: true,
      selectedImageRowIndex: rowIndex,
      selectedImageRowColumnId: columnId,
      rowImage: value,
    }));
    setImageRowIndex(dataRowIndex);
  };

  const handleImageDelete = (rowIndex, columnId) => {
    setState((prevState) => ({
      ...prevState,
      showImgDeleteModal: true,
      selectedImageRowIndex: rowIndex,
      selectedImageRowColumnId: columnId,
      rowImage: null,
    }));
  };
  const closeImgDeleteModal = () => {
    setState((prevState) => ({
      ...prevState,
      showImgDeleteModal: false,
      selectedImageRowIndex: '',
      selectedImageRowColumnId: '',
      rowImage: null,
    }));
  };

  const deleteImgConfirmed = () => {
    const copyTabData = cloneDeep(state?.tabData);
    copyTabData?.[state?.selectedImageRowIndex]?.forEach((element) => {
      if (element?.columnId === state?.selectedImageRowColumnId) {
        element.value = { image: '', fullImage: '' };
      }
    });
    setState((prevState) => ({
      ...prevState,
      tabData: copyTabData,
      showImgDeleteModal: false,
      selectedImageRowIndex: '',
      selectedImageRowColumnId: '',
      rowImage: null,
    }));
  };

  const handleSubmitUpload = async (type) => {
    if (type !== 'close') {
      const croppedFileUrl = await childRef.current.showCroppedImg();

      if (croppedFileUrl) {
        const { imageRes, originalImg } = await croppedFileUrl;

        const originalImgRes = await originalImg;
        const copyTabData = cloneDeep(state?.tabData);
        const foundCell = copyTabData?.[state?.selectedImageRowIndex]?.find((cell) => cell?.columnId === state?.selectedImageRowColumnId);
        if (!foundCell) {
          const sibling = copyTabData?.[state?.selectedImageRowIndex]?.[0];
          copyTabData?.[state?.selectedImageRowIndex].push({
            columnId: state?.selectedImageRowColumnId,
            value: { image: imageRes, fullImage: originalImgRes },
            rowIndex: sibling?.rowIndex ?? state?.selectedImageRowIndex,
            rowId: sibling?.rowId,
            tabId: param?._id,
          });
          setState((prevState) => ({
            ...prevState,
            tabData: copyTabData,
            showImageUpload: false,
          }));
        } else {
          copyTabData?.[state?.selectedImageRowIndex]?.forEach((element) => {
            if (element?.columnId === state?.selectedImageRowColumnId && (imageRowIndex === element?.rowIndex || state?.editingRowId === element?.rowId)) {
              element.value = { image: imageRes, fullImage: originalImgRes };
            }
          });
          setState((prevState) => ({
            ...prevState,
            tabData: copyTabData,
            showImageUpload: false,
          }));
        }
      } else {
        toasterMsg(t('SPEC_TEMP_LIBRARY.IMAGE_REQUIRED'), 'danger');
      }
    } else {
      setState((prevData) => ({
        ...prevData,
        showImageUpload: false,
        selectedImageRowIndex: '',
        selectedImageRowColumnId: '',
        rowImage: null,
      }));
    }
  };

  const eleRender = (column, value, rowIndex, dataRowIndex) => {
    switch (column?.columnDetails?.columnType?.key) {
      case 'Image':
        return (
          <UserInstructionImage
            dataValue={value}
            handleImageUpload={() => handleImageUpload(rowIndex, column._id, value, dataRowIndex)}
            handleImageDelete={() => handleImageDelete(rowIndex, column._id)}
            t={t}
          />
        );
      case 'Dropdown':
        return (
          <div className="common-react-select mb-0">
            <Select
              className="common-select"
              classNamePrefix="common-select"
              options={column?.columnDetails?.options}
              styles={customStyles}
              placeholder={`${t('CARE_LABELS.TABLE.SYMBOL_TABLE.SELECT_CARE_TYPE')} ${column?.name}`}
              menuPosition="fixed"
              getOptionLabel={(option) => `${option?.name}`}
              getOptionValue={(option) => `${option?.name}`}
              name="careType"
              value={value || null}
              onChange={(selectedOption) => handleChange(selectedOption, rowIndex, column?._id, false)}
            />
          </div>
        );
      case 'Multi_select_Dropdown': {
        const options = column?.columnDetails?.options?.map((option) => ({
          label: option?.name,
          value: option?.name,
        }));
        return (
          <MultiSelect
            className="product-custom-multiselect common-new-multiselect"
            options={options || []}
            placeholder={t('CARE_LABELS.TABLE.SYMBOL_TABLE.SELECT_CARE_TYPE')}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.value}
            menuPosition="fixed"
            onChange={(selectedOption) => handleChange(selectedOption, rowIndex, column?._id, false)}
            value={value || []}
          />
        );
      }
      case 'Status':
        return (
          <div className="background-td-color w-100" style={{ backgroundColor: column?.columnDetails?.status?.colourHexCode }}>
            <span>{column?.columnDetails?.status?.colourName}</span>
          </div>
        );
      case 'Radio_button':
        return (
          <div className="d-flex align-items-center">
            {column?.columnDetails?.options?.map((option, i) => {
              const isChecked = value?.name === option?.name;
              return (
                <p className="custom-radio mb-0 mr-2" key={`${rowIndex}_${column?._id}_${i}`}>
                  <input
                    type="radio"
                    id={`radio_${rowIndex}_${column?._id}_${i}`}
                    name={`radio_${rowIndex}_${column?._id}_${i}`}
                    checked={isChecked}
                    onChange={() => handleChange(option, rowIndex, column?._id, false)}
                  />
                  <label htmlFor={`radio_${rowIndex}_${column?._id}_${i}`} className="radio f-14 text-black">
                    {option.name}
                  </label>
                </p>
              );
            })}
          </div>
        );
      case 'Date_picker':
        return (
          <CustomDatePicker
            keyProp={`date_${rowIndex}_${column?._id}`}
            dateFormat="dd-MMM-yyyy"
            valueProp={value || null}
            placeholder={t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.DATE_PLACEHOLDER')}
            onChange={(e) => handleChange(e, rowIndex, column?._id, true)}
          />
        );
      case 'Textbox':
        return (
          <div className="common-input-control mb-0">
            <input
              type="text"
              maxLength={column?.columnDetails?.characterLimit}
              value={value || ''}
              onChange={(e) => handleChange(e?.target?.value, rowIndex, column?._id, false)}
              className="form-control text-bordered"
            />
            <span className="character-show">
              {value?.length || 0}/{column?.columnDetails?.characterLimit}
            </span>
          </div>
        );
      default:
        return (
          <div className="common-input-control mb-0">
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleChange(e?.target?.value, rowIndex, column?._id, false)}
              className="form-control  text-bordered"
            />
          </div>
        );
    }
  };

  const renderValue = (column, value, rowId, rowIndex, dataRowIndex) => {
    if (state?.editingRowId === rowId || (state?.addNewRow && !rowId)) {
      return eleRender(column, value, rowIndex, dataRowIndex);
    }
    switch (column?.columnDetails?.columnType?.key) {
      case 'Status':
        return (
          <div className="background-td-color w-100" style={{ backgroundColor: column?.columnDetails?.status?.colourHexCode }}>
            <span>{column?.columnDetails?.status?.colourName}</span>
          </div>
        );
      case 'Radio_button':
      case 'Dropdown':
        return value?.name || '';
      case 'Image':
        return <img src={setImage(value?.image) || noImage} alt={column?.name} width="70px" height="70px" />;
      case 'Multi_select_Dropdown':
        return value?.map((val) => val?.label).join(', ') || '';
      case 'Date_picker':
        return (value && moment(value).format('DD-MM-YYYY')) || '';
      default:
        if (!value || typeof value === 'object') {
          return '';
        }
        if (typeof value === 'string' && value.length > 150) {
          return shortenandHoverString(value, 150, 'right');
        }
        return value;
    }
  };

  const checkColour = (element, value) => {
    switch (element?.colourValue?._id) {
      case 'greater_than':
        return parseFloat(element?.startPercentage) <= parseFloat(value) && element?.colourType;
      case 'between':
        return parseFloat(element?.startPercentage) <= parseFloat(value) && parseFloat(element?.endPercentage) >= parseFloat(value) && element?.colourType;
      case 'less_than':
        return parseFloat(element?.startPercentage) >= parseFloat(value) && element?.colourType;
      case 'equal':
        return parseFloat(element?.startPercentage) === parseFloat(value) && element?.colourType;
      default:
        return '';
    }
  };

  const checkForColour = (column, value) => {
    let sourceColour = '';
    if (['Number_Decimal', 'Number_percentage'].includes(column?.columnDetails?.columnType?.key) && column?.columnDetails?.colourArray?.length > 0) {
      sourceColour = 'cell-grey';
      column?.columnDetails?.colourArray?.forEach((element) => {
        if (value) {
          const colourName = checkColour(element, parseFloat(value));
          if (colourName === 'green') {
            sourceColour = 'cell-green';
          } else if (colourName === 'red') {
            sourceColour = 'cell-red';
          } else if (colourName === 'amber') {
            sourceColour = 'cell-amber';
          }
        }
      });
      return sourceColour;
    }
  };

  return (
    <>
      {state?.showLoader ? (
        <Loader />
      ) : (
        <div className="table-responsive">
          <table id="care-user-table" className="table  common-custom-table">
            <thead>
              <tr>
                <th aria-label="check" className="w-50px">
                  <div className="common-checkbox margin-auto">
                    <input type="checkbox" onChange={handleSelectAll} checked={state?.isSelectAll} name="check_all_UserList" id="check_all_UserList" />
                    <label htmlFor="check_all_UserList" className="chklabel">
                      <span />
                    </label>
                  </div>
                </th>
                {state?.columns?.map((column, columnIndex) => (
                  <th
                    key={column?._id}
                    className={`w-${column?.name === 'image' ? '100px' : '150px'} ${state.columns?.length > 6 && columnIndex === 0 ? 'first-fix-col' : ''}`}
                  >
                    {column?.name}
                  </th>
                ))}
                <th className={state?.columns?.length > 6 ? 'last-fix-col w-100px' : 'w-100px'} />
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((row, rowIndex) => {
                  const rowId = rows?.[rowIndex]?.[0]?.rowId;
                  return (
                    <tr key={rowIndex}>
                      <td>
                        <div className="common-checkbox margin-auto">
                          <input
                            type="checkbox"
                            name={`check_${rowId}`}
                            id={`check_${rowId}`}
                            onChange={() => handleRowSelection(rowId)}
                            checked={state?.selectedIdArray?.includes(rowId)}
                          />
                          <label htmlFor={`check_${rowId}`} className="chklabel">
                            <span />
                          </label>
                        </div>
                      </td>
                      {state?.columns?.map((column, columnIndex) => {
                        const data = rows[rowIndex]?.find((d) => d?.columnId === column?._id);
                        const value = data?.value || null;
                        return (
                          <td
                            key={column?._id}
                            className={`${state?.columns.length > 6 && columnIndex === 0 ? 'first-fix-col' : ''} ${checkForColour(column, value)}`}
                          >
                            {renderValue(column, value, rowId, rowIndex, data?.rowIndex)}
                            {saveClicked &&
                              (!rowId || rowId === state?.editingRowId) &&
                              column?.columnDetails?.isRequired &&
                              !value &&
                              column?.columnDetails?.columnType?.key !== 'Status' && (
                                <span className="error-message mt-2">{`${column?.name} ${t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.REQUIRED_FIELD')}`}</span>
                              )}
                          </td>
                        );
                      })}
                      <td className={state?.columns?.length > 6 ? 'last-fix-col text-right' : 'text-right'}>
                        {state?.editingRowId === rowId || (state?.addNewRow && !rowId) ? (
                          <div className="d-flex supporting-action justify-content-end">
                            <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.SAVE')}>
                              <button aria-label="button" type="button" className="btn p-0" onClick={() => handleSaveClick(row)}>
                                <i className="icon-Save-1 f-22" />
                              </button>
                            </CustomTooltip>
                            <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.CANCEL')}>
                              <button aria-label="button" type="button" className="btn p-0" onClick={() => handleCancelClick(row, rowIndex)}>
                                <i className="icon-close f-16" />
                              </button>
                            </CustomTooltip>
                          </div>
                        ) : (
                          <div className="d-flex supporting-action justify-content-end">
                            <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.TABLE.VIEW_ACTION.EDIT')}>
                              <button
                                aria-label="button"
                                type="button"
                                className="btn p-0"
                                disabled={state?.editingRowId === rowId || state?.addNewRow || !rowId}
                                onClick={() => handleEditClick(rowId)}
                              >
                                <i className="icon-Edit-1 f-20" />
                              </button>
                            </CustomTooltip>
                            <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.TABLE.VIEW_ACTION.DELETE')}>
                              <button aria-label="button" type="button" className="btn p-0" onClick={() => handleDeleteMultiple(rowId)}>
                                <i className="icon-Delete f-20" />
                              </button>
                            </CustomTooltip>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={(state.columns.length || 0) + 2}>
                    <p className="text-center font-medium m-0">
                      {`${t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.NO')} ${state?.tabName ? state?.tabName : ''} ${t(
                        'USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.NO_LIBRARY_ENTRY_FOUND',
                      )}`}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {state?.showImageUpload && (
        <Modal className="common-custom-modal modal-medium-3x" size="lg" show={state?.showImageUpload} backdrop="static" centered>
          <Modal.Header>
            <div className="d-flex  justify-content-center align-items-center w-100">
              <Modal.Title className="expand-modal-title ml-auto">
                <span className="common-title">{t('SPEC_TEMP_LIBRARY.UPLOAD_IMAGE')}</span>
              </Modal.Title>
              <span onClick={() => handleSubmitUpload('close')} className="icon-close common-close-icon ml-auto cursor-pointer" />
            </div>
          </Modal.Header>

          <Modal.Body>
            <BomImageDropzone
              ref={childRef}
              title=""
              onSubmit={handleSubmitUpload}
              image={setImage(state?.rowImage?.fullImage)}
              acceptedFileExtenstion=".jpeg,.png,.jpg"
            />
          </Modal.Body>
        </Modal>
      )}
      {state?.showImgDeleteModal && (
        <ConfirmationPopup
          onOpen={state?.showImgDeleteModal}
          onConfirm={deleteImgConfirmed}
          title={t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.IMAGE_DELETE_TITLE')}
          message={t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.IMAGE_DELETE_MESSAGE')}
          toggle={closeImgDeleteModal}
          showThirdMsg
        />
      )}
    </>
  );
}
